import React, { useEffect, useState, useRef } from "react";
import { Menu } from "../../components/menu";
import OrderSummary from "../../components/menu/orderSummary";
import { useDispatch, useSelector } from "react-redux";
import {
  getCategoriesAsync,
  getItemsAsync,
  getItemDetailsAsync,
} from "../../redux/features/restaurants/restaurantThunks";
import {
  selectBranchDetails,
  selectCartItems,
  selectShowProductDetail,
  selectItems,
  selectProcessedReorder,
  selectProcessedOrderId,
} from "../../redux/features/restaurants/restaurantSelectors";
import { selectPastOrderDetails } from "../../redux/features/order/orderSelectors";
import Product from "../../components/menu/product";
import {
  setCartItems,
  setCartSubTotal,
  setCartVariantItems,
  setProcessedReorder,
  setProcessedOrderId,
} from "../../redux/features/restaurants/restaurantSlice";
import { generateVariantId } from "../../utils/menu";
import { setOrderType } from "../../redux/features/order/orderSlice";

export const PlaceOrder = () => {
  const branchDetails = useSelector(selectBranchDetails);
  const cart = useSelector(selectCartItems);
  const showProductDetail = useSelector(selectShowProductDetail);
  const pastOrderDetails = useSelector(selectPastOrderDetails);
  const menuItems = useSelector(selectItems);
  const processedReorder = useSelector(selectProcessedReorder);
  const processedOrderId = useSelector(selectProcessedOrderId);

  const [loading, setLoading] = useState(true);
  const [processingReorder, setProcessingReorder] = useState(false);

  const dispatch = useDispatch();
  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    // If price already includes KWD, return as is
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }

    // Convert to number and format with 3 decimal places, then add KWD prefix
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  useEffect(() => {
    if (branchDetails) {
      dispatch(
        getCategoriesAsync(
          branchDetails?.restaurant_id,
          branchDetails?.branch_id
        )
      );
      dispatch(
        getItemsAsync(
          branchDetails?.restaurant_id,
          branchDetails?.branch_id,
          setLoading
        )
      );
    }
  }, [branchDetails, dispatch]);

  // Calculate and update subtotal based on current cart
  const updateCartSubtotal = (cartItems) => {
    const subtotal = cartItems?.reduce((total, item) => {
      // Guard against undefined or invalid price format
      const itemPrice = typeof item.price === 'string' && item.price.includes('KWD') 
        ? parseFloat(item.price.replace("KWD ", "")) 
        : parseFloat(item.price || 0);

      // Handle case where item has_variants is true but no variants are selected
      if (item.has_variants && item.items && item.items.length > 0) {
        // For variant items with actual variants
        const itemTotal = item.items?.reduce((sum, variant) => {
          // Ensure variant has a valid quantity
          const variantQuantity = variant.quantity || 0;
          return sum + itemPrice * variantQuantity;
        }, 0);
        return total + itemTotal;
      } else {
        // For simple items or items marked as variants but without actual variants
        return total + itemPrice * (item.quantity || 1); // Default to 1 if quantity is missing
      }
    }, 0);

    // Ensure subtotal is not NaN
    const validSubtotal = isNaN(subtotal) ? 0 : subtotal;
    dispatch(setCartSubTotal(validSubtotal.toFixed(3)));
  };

  // Function to add a simple item to cart
  const addItemToCart = (item, quantity = 1) => {
    const updatedItem = {
      ...item,
      quantity,
      price: formatPrice(item.price),
    };
    dispatch(setCartItems(updatedItem));
  };

  // Function to add a variant item to cart
  const addVariantItemToCart = async (item, variantData, quantity = 1) => {
    try {
      // First get the item details to ensure we have the correct variant structure
      await dispatch(
        getItemDetailsAsync(
          item.restaurant_id || branchDetails?.restaurant_id,
          item.branch_id || branchDetails?.branch_id,
          item.id,
          true
        )
      );

      // Format variant selections from the order data
      const selectedVariants = [];

      // Process each variant type (custom_variant, custom_variant2, etc.)
      if (variantData.custom_variant && variantData.custom_variant.length > 0) {
        selectedVariants.push({
          variant_id: parseInt(variantData.custom_variant_id),
          variant_type: "custom",
          options: variantData.custom_variant.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      if (
        variantData.custom_variant2 &&
        variantData.custom_variant2.length > 0
      ) {
        selectedVariants.push({
          variant_id: parseInt(variantData.custom_variant_id2),
          variant_type: "custom",
          options: variantData.custom_variant2.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      if (
        variantData.custom_variant3 &&
        variantData.custom_variant3.length > 0
      ) {
        selectedVariants.push({
          variant_id: parseInt(variantData.custom_variant_id3),
          variant_type: "custom",
          options: variantData.custom_variant3.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      // Other possible variant types
      if (variantData.size_variant && variantData.size_variant.length > 0) {
        selectedVariants.push({
          variant_id: parseInt(variantData.size_variant_id),
          variant_type: "size",
          options: variantData.size_variant.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      if (
        variantData.topping_variant &&
        variantData.topping_variant.length > 0
      ) {
        selectedVariants.push({
          variant_id: parseInt(variantData.topping_variant_id),
          variant_type: "topping",
          options: variantData.topping_variant.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      if (variantData.side_variant && variantData.side_variant.length > 0) {
        selectedVariants.push({
          variant_id: parseInt(variantData.side_variant_id),
          variant_type: "side",
          options: variantData.side_variant.map((option) => ({
            option_id: option.option_id,
            option_name: option.name_en,
            option_quantity: option.option_quantity,
            unit_price: parseFloat(option.unit_price || 0),
          })),
        });
      }

      // Create a valid variantItems structure 
      // Either using selected variants or creating a default variant structure if none are selected
      let variantItems;
      let variantId;

      if (selectedVariants.length > 0) {
        // Use the selected variants if available
        variantId = generateVariantId(selectedVariants, "");
        
        variantItems = {
          id: item.id,
          name: item.name_en,
          price: formatPrice(item.price || variantData.unit_price),
          has_variants: true,
          items: [
            {
              variantId,
              selectedVariant: selectedVariants,
              quantity: quantity,
              instructions: "",
            },
          ],
        };
      } else if (item.has_variants && item.variants && item.variants.length > 0) {
        // If no variants were selected but the item has variants available,
        // create a variant structure using the first variant from the item data
        
        // Get the first variant from the item's variants array
        const firstVariant = item.variants[0];
        
        // Create a default variant with options but with 0 quantities
        const defaultVariant = {
          variant_type: "custom",
          variant_id: firstVariant.id,
          options: firstVariant.options.map(option => ({
            option_id: option.id,
            option_name: option.name_en,
            option_quantity: 0,
            unit_price: parseFloat(option.unit_price || 0)
          }))
        };
        
        variantId = generateVariantId([defaultVariant], "");
        
        variantItems = {
          id: item.id,
          name: item.name_en,
          price: formatPrice(item.price || variantData.unit_price),
          has_variants: true,
          items: [
            {
              variantId,
              selectedVariant: [defaultVariant],
              quantity: quantity,
              instructions: "",
            },
          ],
        };
      }

      // Dispatch to add the variant item to cart
      if (variantItems) {
        dispatch(setCartVariantItems(variantItems));
      }
    } catch (error) {
      console.error("Error adding variant item to cart:", error);
    }
  };

  // Reset processing state when pastOrderDetails changes
  useEffect(() => {
    // If we have a new order ID or the cart is empty, we should reset the state
    if (pastOrderDetails && pastOrderDetails?.order_id !== processedOrderId) {
      console.log("Resetting reorder processing for new order or empty cart");
      dispatch(setProcessedReorder(false));
      dispatch(setProcessedOrderId(pastOrderDetails?.order_id));
    }
  }, [pastOrderDetails, cart, dispatch, processedOrderId]);

  useEffect(() => {
    if (cart.length === 0) {
      dispatch(setProcessedReorder(false));
      dispatch(setProcessedOrderId(null));
    }
  }, []);

  // Process the reorder if pastOrderDetails is available
  useEffect(() => {
    const processPastOrder = async () => {
      // Process the reorder if:
      // 1. We have valid pastOrderDetails
      // 2. Not currently processing
      // 3. Have menu items to work with
      // 4. Haven't processed this order yet
      // 5. Restaurant ID matches (to ensure we're ordering from the right restaurant)
      if (
        pastOrderDetails &&
        Object.keys(pastOrderDetails)?.length > 0 &&
        !processingReorder &&
        menuItems &&
        menuItems.length > 0 &&
        !processedReorder &&
        pastOrderDetails?.restaurant_id === menuItems[0].restaurant_id
      ) {
        console.log("Processing reorder", pastOrderDetails?.order_id);
        setProcessingReorder(true);
        dispatch(setProcessedReorder(true)); // Mark as processed in Redux
        dispatch(setProcessedOrderId(pastOrderDetails?.order_id)); // Store order ID in Redux

        try {
          // Extract ordered items from pastOrderDetails
          const orderItems = pastOrderDetails?.order_item || [];
          let updatedCart = [...cart]; // Start with current cart

          // Process each item
          for (const orderItem of orderItems) {
            // Get the menu item details
            const menuItem = menuItems.find(
              (item) =>
                item.id === orderItem.menu_item.menu_id ||
                item.name_en === orderItem.menu_item.name_en
            );
            if (menuItem) {
              // Check if item has variants
              const hasVariants =
                orderItem.custom_variant?.length > 0 ||
                orderItem.custom_variant2?.length > 0 ||
                orderItem.custom_variant3?.length > 0 ||
                orderItem.size_variant?.length > 0 ||
                orderItem.topping_variant?.length > 0 ||
                orderItem.side_variant?.length > 0 || menuItem.has_variants;
              if (hasVariants) {
                // Add item with variants
                await addVariantItemToCart(
                  {
                    ...menuItem,
                    price: formatPrice(orderItem.unit_price),
                    restaurant_id: orderItem.restaurant_id,
                    branch_id: orderItem.branch_id,
                  },
                  orderItem,
                  orderItem.quantity
                );

                // Update our local cart copy to calculate subtotal later
                const variantItem = {
                  id: menuItem.id,
                  name: menuItem.name_en,
                  price: formatPrice(orderItem.unit_price),
                  has_variants: true,
                  items: [
                    {
                      quantity: orderItem.quantity,
                    },
                  ],
                };
                updatedCart.push(variantItem);
              } else {
                // Add simple item
                const simpleItem = {
                  ...menuItem,
                  price: formatPrice(orderItem.unit_price),
                  quantity: orderItem.quantity,
                };
                addItemToCart(simpleItem, orderItem.quantity);

                // Update our local cart copy
                updatedCart.push(simpleItem);
              }
            }
          }
          // Update the subtotal
          updateCartSubtotal(updatedCart);
        } catch (error) {
          console.error("Error processing past order:", error);
        } finally {
          setProcessingReorder(false);
        }
      }
    };

    processPastOrder();
  }, [pastOrderDetails, menuItems, dispatch, processingReorder, cart]); // Added cart to dependencies

  // Set order type to asap when component mounts and cart is empty
  useEffect(() => {
    if (cart.length === 0) {
      dispatch(setOrderType("asap"));
    }
  }, [cart.length, dispatch]);

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <div className="spinner-border animate-spin border-t-4 border-b-4 border-gray-600 w-16 h-16 rounded-full"></div>
        </div>
      ) : (
        <div className="relative">
          <div
            className={`${
              cart.length !== 0 || showProductDetail ? "sm:mr-[547px]" : ""
            }`}
          >
            <Menu branchDetails={branchDetails} />
          </div>
          {cart.length !== 0 && !showProductDetail && <OrderSummary />}
          {showProductDetail && <Product />}
        </div>
      )}
    </>
  );
};
